/**
 * Hotels — the canonical property master.
 *
 * Every row EXPANDS (2026-08-09). The route was already sending 26 fields per
 * property while the table showed 11, so the other two GDS codes, the street
 * address, the trailing-history breakdown and any contract on file were arriving
 * and being thrown away. Same call as the Agreements table: columns stay the ones
 * you scan and compare, everything else is one disclosure click down. See
 * HotelDetail for the panel and features/crm/components/ExpandableRow for the
 * disclosure mechanics.
 */
import { useEffect, useMemo, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import {
  DetailRow,
  DisclosureCell,
  DisclosureHeaderCell,
  TableScroll,
  detailRowId,
  totalColumns,
  useExpandedRows,
  zebraFor,
} from '@/features/crm/components/ExpandableRow';
import { contactHref } from '@/features/crm/lib/contact-href';
import { fmtMaybeInstant } from '@/features/crm/lib/dates';
import {
  HotelDetail,
  adrFor,
  fmtNum,
  fmtRate,
  fmtSpend,
  type Hotel,
} from './HotelDetail';

interface HotelsResult {
  hotels: Hotel[];
  total: number;
  page: number;
  page_size: number;
}

const PAGE_SIZE = 50;
const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'spend', label: 'Spend' },
  { value: 'nights', label: 'Nights' },
  { value: 'bookings', label: 'Bookings' },
  { value: 'last_booking', label: 'Recent' },
  { value: 'name', label: 'Name' },
];

/**
 * What period the booking figures cover — and the reason it is said out loud.
 *
 * Stacie, test-plan step 1b: "Does this bookings/nights/spend/avg nightly default
 * to a set timeframe? Ideally, we'd like last rolling year". The honest answer is
 * that there is no window at all: `hsp.hotel_master` stores these as LIFETIME
 * rollups synced from Domo, and the API reads Postgres, so no request parameter
 * could narrow them — the windowed number does not exist at that grain.
 *
 * Worth stating on screen rather than leaving to be assumed, because the adjacent
 * Rate Opportunities tab defaults to a TRAILING 12 MONTHS. Two tabs in the same
 * module were quietly counting different periods with nothing to distinguish them,
 * which is how a figure from one gets compared against a figure from the other.
 *
 * A real rolling-year column needs the sync to emit windowed figures; until then
 * this note is the whole fix, and it is a better state than an implied recency.
 */
export const ALL_TIME_TITLE =
  'All-time total since the first recorded booking — not a recent window';

export const BOOKING_PERIOD_NOTE =
  'Bookings, Nights, Spend and Avg nightly are all-time totals since the first recorded booking — not a rolling year.';

/**
 * The table's columns, as data rather than hand-written `<th>`s — so the detail
 * row's colSpan is derived from the same list that renders the header and cannot
 * drift out of step with it. Exported so a test can pin that.
 */
export const HOTEL_COLUMNS: {
  key: string;
  label: string;
  align?: 'right';
  /** Hover text. Used for the one thing a header word cannot carry: the period. */
  title?: string;
}[] = [
  { key: 'property', label: 'Property' },
  { key: 'location', label: 'City / State' },
  // Brand removed 2026-08-11 (Stacie): the chain dimension is not how sourcing is
  // worked. Still searchable — see the search placeholder.
  // Avg nightly sits HERE, ahead of the identifier columns, for item 34: "Avg
  // Nightly should always be visible on the main Hotels view". It is the one
  // figure that decides whether a property is worth opening, and it was second
  // from the right — the far side of the overflow item 31 describes, so on a
  // laptop it was never on screen. Item 31's fix makes the table scrollable
  // without hunting for the bar; this makes the number visible without scrolling
  // at all. The cost is that it no longer sits beside the other totals, which is
  // a real legibility loss and the reason it keeps ALL_TIME_TITLE — the tooltip
  // is now the only thing next to it saying what period it covers.
  { key: 'adr', label: 'Avg nightly', align: 'right', title: ALL_TIME_TITLE },
  { key: 'sabre', label: 'Sabre' },
  { key: 'phone', label: 'Phone' },
  { key: 'contact', label: 'Contact' },
  // The four booking figures are ALL-TIME totals, not a recent window. See
  // BOOKING_PERIOD_NOTE below for why that is stated on screen rather than
  // assumed.
  {
    key: 'bookings',
    label: 'Bookings',
    align: 'right',
    title: ALL_TIME_TITLE,
  },
  { key: 'nights', label: 'Nights', align: 'right', title: ALL_TIME_TITLE },
  { key: 'spend', label: 'Spend', align: 'right', title: ALL_TIME_TITLE },
  // "Avg nightly" is what we actually PAID — spend / nights, nights-weighted.
  //
  // The "Contract rate" column that used to sit beside it was REMOVED 2026-08-12
  // (Stacie, test-plan step 2a): "cleaner to always have Avg Nightly displayed on
  // the main view and then, when clicking into Hotel... have a list of top clients
  // booking there, if/what their last contracted rate booked was".
  //
  // She is right, and the reason is structural rather than cosmetic: a contract
  // rate is per-CUSTOMER, and this table has one row per PROPERTY. The old column
  // showed one row of the Domo contracts feed picked for its contact quality, so on
  // a property where seven customers each hold a rate it displayed one of the
  // seven, chosen for a reason unrelated to rates. There is no correct single value
  // to put in a per-property cell.
  //
  // Nothing is lost: the per-customer rates are in the detail panel's Agreements
  // section, which lists every customer with an agreement here alongside their
  // rate, and the feed's own row is still in the panel's "Contract record (single
  // row)" group with its caveat attached.
  { key: 'last_booking', label: 'Last booking' },
];

const TOTAL_COLUMNS = totalColumns(HOTEL_COLUMNS.length);

/**
 * Table cells show an em-dash for a missing value; the formatters return null so
 * that `Field` inside the detail panel can make the same decision itself. This
 * is the one-line bridge between the two.
 */
function orDash(value: string | null): string {
  return value ?? '—';
}

export default function HotelsPage() {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [sort, setSort] = useState('spend');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<HotelsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // See ExpandableRow for why this is component state and not URL state.
  // `collapseAll` is destructured so the fetch effect can depend on a stable
  // reference — depending on the hook's return object would re-run the effect
  // every render, which is a refetch loop rather than a lint nit.
  const expanded = useExpandedRows();
  const { collapseAll } = expanded;

  // Debounce the search box so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Any new search resets to page 1 (stale page numbers over a smaller result
  // set would show an empty page).
  useEffect(() => {
    setPage(1);
  }, [debounced, sort]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(PAGE_SIZE),
      sort,
    });
    if (debounced) params.set('q', debounced);
    apiFetch<HotelsResult>(`/api/btp/hotels?${params}`)
      .then((d) => {
        if (!cancelled) {
          setData(d);
          // Collapse on a re-query: paging or searching replaces the rows, and a
          // stale id left expanded would open whichever unrelated property
          // happened to reuse it.
          collapseAll();
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced, sort, page, collapseAll]);

  const total = data?.total ?? 0;
  const rows = data?.hotels ?? [];
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  const meta = useMemo(() => {
    if (loading) return 'Loading…';
    if (total === 0) return 'No hotels';
    return `${rangeStart.toLocaleString()}–${rangeEnd.toLocaleString()} of ${total.toLocaleString()}`;
  }, [loading, total, rangeStart, rangeEnd]);

  return (
    <div className="page" data-testid="hotels-page">
      <div className="page-header">
        <h1>Hotels</h1>
        <p className="page-intro">
          The canonical hotel master — every property in the Andavo booking
          universe, with GDS codes and trailing booking history. Use it to look
          up a property before sourcing a rate or opening an RFP, and open a row
          for its full address, every GDS code and any contract on file.
        </p>
      </div>

      <div className="section">
        <div className="section-header">
          <span className="section-label">Property master</span>
          <span className="section-meta">{meta}</span>
        </div>
        <div className="section-body">
          <div
            style={{
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              marginBottom: 14,
            }}
          >
            <div style={{ flex: '1 1 280px', maxWidth: 420 }}>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, city, state, brand, GDS code, phone, or contact…"
                className="search-input"
                aria-label="Search hotels"
                data-testid="hotels-search"
              />
            </div>
            <FilterGroup label="Sort by">
              {SORT_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  onClick={() => setSort(o.value)}
                  className={'pill' + (sort === o.value ? ' active' : '')}
                  data-testid={`hotels-sort-${o.value}`}
                >
                  {o.label}
                </button>
              ))}
            </FilterGroup>
          </div>

          {error && <div className="banner banner-error">{error}</div>}

          {/* Above the table, not below it: a period caveat under a table is read
              after the numbers have already been believed. */}
          <p className="table-note" data-testid="hotels-period-note">
            <span className="muted">{BOOKING_PERIOD_NOTE}</span>
          </p>

          <TableScroll>
            <table className="data-table" data-testid="hotels-table">
              <thead>
                <tr>
                  <DisclosureHeaderCell />
                  {HOTEL_COLUMNS.map((col) => (
                    <th
                      className={col.align === 'right' ? 'right' : undefined}
                      key={col.key}
                      title={col.title}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr className="empty">
                    <td colSpan={TOTAL_COLUMNS}>Loading hotels…</td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr className="empty">
                    <td colSpan={TOTAL_COLUMNS}>
                      {debounced
                        ? `No hotels match “${debounced}”.`
                        : 'No hotels in the master list yet.'}
                    </td>
                  </tr>
                )}
                {!loading &&
                  rows.flatMap((h, i) => {
                    const isOpen = expanded.isExpanded(h.id);
                    const detailId = detailRowId('hotel', h.id);
                    // Data-derived, not :nth-child — an open row inserts a
                    // second <tr>. See ExpandableRow.
                    const zebra = zebraFor(i);

                    const out = [
                      <tr data-zebra={zebra} key={h.id}>
                        <DisclosureCell
                          controls={detailId}
                          label={h.name}
                          onToggle={() => expanded.toggle(h.id)}
                          open={isOpen}
                          testId={`hotels-expand-${h.id}`}
                        />
                        <td style={{ fontWeight: 600 }}>{h.name}</td>
                        <td>
                          {[h.city, h.state].filter(Boolean).join(', ') || '—'}
                        </td>
                        {/* What a night here cost us. Null (not 0) whenever nights
                            is missing or zero — see adrFor. */}
                        <td
                          className="right"
                          data-testid={`hotels-adr-${h.id}`}
                        >
                          {orDash(fmtRate(adrFor(h)))}
                        </td>
                        <td className="mono">{h.sabre_property_code || '—'}</td>
                        <td>{h.phone || '—'}</td>
                        <td>
                          <ContactCell hotel={h} />
                        </td>
                        <td className="right">{orDash(fmtNum(h.bookings))}</td>
                        <td className="right">{orDash(fmtNum(h.nights))}</td>
                        <td className="right" style={{ fontWeight: 600 }}>
                          {orDash(fmtSpend(h.spend_usd))}
                        </td>
                        <td>{orDash(fmtMaybeInstant(h.last_booking))}</td>
                      </tr>,
                    ];

                    if (isOpen) {
                      out.push(
                        <DetailRow
                          colSpan={TOTAL_COLUMNS}
                          id={detailId}
                          key={`${h.id}-detail`}
                          testId={`hotels-detail-${h.id}`}
                          zebra={zebra}
                        >
                          <HotelDetail hotel={h} />
                        </DetailRow>,
                      );
                    }
                    return out;
                  })}
              </tbody>
            </table>
          </TableScroll>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 14,
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}
            >
              {total > 0
                ? `Page ${page.toLocaleString()} of ${totalPages.toLocaleString()}`
                : ''}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                className="pill"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={loading || page <= 1}
                data-testid="hotels-prev-page"
              >
                Previous
              </button>
              <button
                className="pill"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={loading || page >= totalPages}
                data-testid="hotels-next-page"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────

/**
 * Who to call at this property. Name on top, email beneath as a `mailto:` link,
 * phone under that.
 *
 * REWRITTEN 2026-08-13 after a pre-open review, and both changes matter:
 *
 * 1. It reads `display_contact`, not `contact_*`. The raw `contact_*` fields are the
 *    single Domo contracts row, and this cell used to render them directly — so once
 *    someone recorded a contact of our own with a corrected number, the table still
 *    showed the feed's stale person while the detail panel showed the correction.
 *    Whichever one you believed depended on whether you had expanded the row. The
 *    server now applies the same supersession rule the panel uses, and hands over one
 *    answer plus where it came from.
 *
 * 2. The feed row is LABELLED. An unmarked contact reads as one we stand behind; this
 *    one may be months stale and cannot be corrected in place. The panel had a
 *    provenance column from the start and the table contradicted it.
 *
 * The all-departed state is deliberately its own message rather than an em dash: "we
 * knew someone here and they have gone" is a true, actionable answer that should
 * prompt recording a replacement, and a dash would report it as "we never knew
 * anyone".
 */
function ContactCell({ hotel }: { hotel: Hotel }) {
  const c = hotel.display_contact;

  if (!c) {
    if (hotel.display_contact_all_departed) {
      return (
        <span
          data-testid="hotel-contact-all-departed"
          style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}
          title="Every contact recorded for this property has left. Open the row to add a replacement."
        >
          Contact has left
        </span>
      );
    }
    return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <span style={{ fontWeight: 600 }}>
        {c.name}
        {c.provenance === 'feed' && (
          <>
            {' '}
            <span
              className="badge badge-neutral badge-no-dot"
              data-testid="hotel-contact-feed-badge"
              title="From the Domo contract feed, not a contact we keep. Open the row to add one of ours."
            >
              Feed
            </span>
          </>
        )}
      </span>
      {c.email && (
        // Encoded, never interpolated — `mailto:` reads `?` as the start of a header
        // list, so a raw value can add a recipient the sender never typed. This cell
        // is the one that reaches the widest audience, and it was the last raw sink.
        <a
          href={contactHref('mailto', c.email)}
          style={{ fontSize: 'var(--fs-sm)' }}
        >
          {c.email}
        </a>
      )}
      {c.phone && (
        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
          {c.phone}
        </span>
      )}
    </div>
  );
}

// `RateCell` lived here — the Contract rate cell, with a "Preferred" badge.
// Deleted 2026-08-12 with its column (see HOTEL_COLUMNS). Both halves survive in
// the detail panel's "Contract record (single row)" group, which shows the same
// figure as "Rate on this record" and the flag as its own Preferred field — with
// the caveat about WHY that number is only one of several attached on screen,
// which a table cell had no room to say.

function FilterGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 'var(--fs-xs)',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          color: 'var(--text-muted)',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {children}
      </div>
    </div>
  );
}
